import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useXeroBackfill, type XeroBackfillResult } from '@/hooks/useXeroConnection';
import { formatDateInputInTz } from '@/utils/timezone';

/** First day of the current financial year for the tenant's country. */
function financialYearStart(countryCode: string, timezone: string): string {
  const today = formatDateInputInTz(new Date(), timezone); // yyyy-MM-dd
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const fyStartMonth: Record<string, number> = { AU: 7, NZ: 4, GB: 4, IE: 1, US: 1, CA: 1, SG: 1, ZA: 3 };
  const startMonth = fyStartMonth[countryCode] ?? 1;
  const startYear = month >= startMonth ? year : year - 1;
  return `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
}

interface XeroBackfillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Sync invoices issued before Xero was connected. Always previews first
 * (a dry run with counts and a rough duration) so nobody double-enters
 * a year of invoices by accident.
 */
export function XeroBackfillDialog({ open, onOpenChange }: XeroBackfillDialogProps) {
  const { locale, timezone } = useCompanyLocale();
  const backfill = useXeroBackfill();
  const [fromDate, setFromDate] = useState('');
  const [preview, setPreview] = useState<XeroBackfillResult | null>(null);

  useEffect(() => {
    if (open) {
      setFromDate(financialYearStart(locale.countryCode, timezone));
      setPreview(null);
    }
  }, [open, locale.countryCode, timezone]);

  const runPreview = async () => {
    try {
      const result = await backfill.mutateAsync({ fromDate, dryRun: true });
      setPreview(result);
    } catch (e) {
      toast({ title: 'Could not preview', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const runForReal = async () => {
    try {
      const result = await backfill.mutateAsync({ fromDate, dryRun: false });
      toast({
        title: `${result.queued} invoice${result.queued === 1 ? '' : 's'} queued for Xero`,
        description: result.eta_minutes > 1 ? `They will arrive over the next ${result.eta_minutes} minutes.` : 'They will arrive within a minute or two.',
      });
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Could not start the sync', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  const busy = backfill.isPending;
  const nothingToDo = preview !== null && preview.invoices === 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sync history to Xero</DialogTitle>
          <DialogDescription>
            Send invoices issued before Xero was connected, with their payments. Invoices already in Xero are
            skipped. If your bookkeeper has entered any of these by hand, void one copy afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="xero-backfill-from">Invoices issued on or after</Label>
            <Input
              id="xero-backfill-from"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPreview(null);
              }}
              disabled={busy}
            />
          </div>

          {preview && (
            <div className="rounded-lg border p-3 text-sm">
              {nothingToDo ? (
                <p className="text-muted-foreground">
                  Nothing to send: every issued invoice since that date is already in Xero
                  {preview.already_in_xero ? ` (${preview.already_in_xero} found)` : ''}.
                </p>
              ) : (
                <ul className="space-y-1">
                  <li>
                    <span className="font-medium">{preview.invoices}</span> invoice{preview.invoices === 1 ? '' : 's'} to send
                  </li>
                  <li>
                    <span className="font-medium">{preview.payments}</span> payment{preview.payments === 1 ? '' : 's'} and{' '}
                    <span className="font-medium">{preview.credit_notes}</span> refund{preview.credit_notes === 1 ? '' : 's'} to apply
                  </li>
                  {preview.already_in_xero > 0 && (
                    <li className="text-muted-foreground">{preview.already_in_xero} already in Xero (skipped)</li>
                  )}
                  <li className="text-muted-foreground">
                    About {Math.max(1, preview.eta_minutes)} minute{preview.eta_minutes === 1 ? '' : 's'}, paced to stay inside Xero's limits
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          {!preview || nothingToDo ? (
            <Button onClick={() => void runPreview()} disabled={busy || !fromDate}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Preview
            </Button>
          ) : (
            <Button onClick={() => void runForReal()} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send {preview.invoices} invoice{preview.invoices === 1 ? '' : 's'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
